<template>
  <table class="entsys--table mobile--kipa-hide-header">
    <thead>
      <tr class="hidden-md-and-down">
        <th
          v-for="header in headersProp"
          :key="header.text"
          class="text-left"
          style
        >
          {{ header.text }}
        </th>
      </tr>
    </thead>
    <tbody>
      <tr v-show="loading || items.length == 0" class="pa-0">
        <td colspan="99" class="pa-0">
          <v-progress-linear
            :active="loading"
            color="secondary"
            indeterminate
          />

          <div
            v-if="loading && items.length == 0"
            class="text-center py-4 muted--text"
          >
            {{ $t("common:loading.messsage", "Loading...", "Loading message") }}
          </div>
          <div
            v-else-if="!loading && items.length == 0"
            class="text-center py-4 muted--text"
          >
            <slot name="result-text">
              {{
                $t(
                  "common:NoResults.messsage",
                  "No data available",
                  "No results message"
                )
              }}
            </slot>
          </div>
        </td>
      </tr>
      <template v-for="(item, i) in items">
        <tr :key="item.id + '-row'">
          <td style="width: 35em;">
            <router-link
              :to="{ name: 'HorseProfilePage', params: { horseId: item.id } }"
              class="no-default-link-style d-block"
            >
              <AppShowHorseWithDetails :horse="item" />
            </router-link>
          </td>

          <td class="width-nowaste hidden-md-and-down">
            <AppValidationAlert
              :check-valid="item.currentSeasonLicenceValid"
              :valid="$t('common:valid.badge', 'Valid')"
              no-invalid
            />
          </td>

          <td class="width-nowaste  hidden-md-and-down">
            {{ item.identifier }}
          </td>

          <td class="width-nowaste hidden-md-and-down">
            <AppShowHorseAccountStatus :horse="item" />
          </td>

          <td class="hidden-lg-and-up text-center">
            <button
              class="btn-focus"
              :aria-label="$t('common:expandButton.alt')"
              @click.prevent="expand($event, item.id)"
            >
              <v-icon color="primary">
                $iconCircleCaretDown
              </v-icon>
            </button>
          </td>
        </tr>

        <tr
          v-if="expanded.includes(item.id)"
          :key="item.id + '-expanded'"
          class="hidden-lg-and-up"
        >
          <td colspan="99" class="pa-0">
            <v-card color="strong">
              <v-card-text>
                <div class="my-1">
                  <b>{{
                    $t(
                      "horses:horseForm.subheading.LicenceNr",
                      "Licence / Reg.nr",
                      "Section subheading for licence / rge.nr fields"
                    )
                  }}</b>
                  {{ item.identifier }}
                </div>
                <div class="my-1">
                  <b>{{
                    $t(
                      "horses:horseForm.subheading.YearlyLicence",
                      "Yearly licence",
                      "Section subheading for yearly licence fields"
                    )
                  }}</b>
                  <AppValidationAlert
                    :check-valid="item.currentSeasonLicenceValid"
                    valid="Valid"
                    no-invalid
                  />
                </div>
                <div class="my-1">
                  <b>{{
                    $t(
                      "horses:horseForm.subheading.Status",
                      "Status",
                      "Section subheading for status fields"
                    )
                  }}</b>
                  <AppShowHorseAccountStatus :horse="item" class="ml-1" />
                </div>
              </v-card-text>
            </v-card>
          </td>
        </tr>

        <tr
          v-if="showBannerConditionally(i, 'horses')"
          :key="'-ad-' + item.id"
          class="no-hover hidden-lg-and-up pa-0"
          style="border: 0;"
        >
          <td colspan="99" class="pa-0">
            <AppShowBanners
              placement-name="HORSES"
              :size="$vuetify.breakpoint.mdAndDown ? '150px' : undefined"
              :force-row="$vuetify.breakpoint.mdAndDown"
              class="py-4"
            />
          </td>
        </tr>
      </template>
    </tbody>
  </table>
</template>
<script>
import gql from "fraql";
import { useBannerHelpers } from "@composables/banner_helpers";

import AppValidationAlert from "@/components/elements/AppValidationAlert.vue";
import AppShowHorseAccountStatus from "@/components/elements/AppShows/horse/AppShowHorseAccountStatus.vue";
import AppShowBanners from "@/components/elements/banners/AppShowBanners.vue";
import AppShowHorseWithDetails from "@/components/elements/AppShows/horse/AppShowHorseWithDetails.vue";
// $t("common:loading.messsage", "Loading....", "Loading message")
export default {
  name: "SearchTemplateHorses",
  components: {
    AppValidationAlert,
    AppShowHorseAccountStatus,
    AppShowHorseWithDetails,
    AppShowBanners
  },
  props: {
    items: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  setup() {
    const { showBannerConditionally } = useBannerHelpers();
    return { showBannerConditionally };
  },
  data() {
    return {
      expanded: []
    };
  },
  computed: {
    headersProp() {
      return [
        {
          value: "name",
          text: this.$t("horses:fields.name.label", "Horse", "Name field label")
        },
        {
          value: "currentSeasonLicenceValid",
          text: this.$t(
            "horses:fields.YearlyLicence.label",
            "Yearly licence",
            "Yearly licence field label"
          )
        },
        {
          value: "licenceNumber",
          text: this.$t(
            "horses:fields.LicenceNumber.label",
            "Licence / Reg.nr",
            "Licence number field label"
          )
        },
        {
          value: "status",
          text: this.$t(
            "horses:fields.status.label",
            "Status",
            "Status field label"
          )
        }
      ];
    }
  },
  methods: {
    expand(evt, id) {
      if (this.expanded.includes(id)) {
        this.expanded = this.expanded.filter(i => i != id);
      } else {
        this.expanded.push(id);
      }
      evt.target.classList.toggle("counter-rotate");
    }
  },
  fragments: {
    horses: gql`
       fragment _ on Horse {
          id
          identifier
          identifierHeppaStatus
          birthName
          ${AppShowHorseWithDetails.fragments.horse}
          ${AppShowHorseAccountStatus.fragments.horse}
          currentSeasonLicenceValid
          licenceNumber
       }
      `
  }
};
</script>
